import React, { useEffect, useState } from 'react'

import { connect } from 'react-redux'
import pluralize from 'pluralize'
import { Link, withRouter, RouteComponentProps } from 'react-router-dom'
import { Container, Table, Card, Form, Button, Badge } from 'react-bootstrap'

import { userCan } from 'src/store/user/userActions'
import ChannelService from 'src/services/ChannelService'

import { paginate } from 'src/utils/filters'
import { route } from 'src/utils/routes'
import { basicSearch } from 'src/utils/search'

import { IState } from 'src/store'
import { IChannel } from 'src/services/types/channel'
import { IUser } from 'src/services/types/user'

import Error401 from 'src/pages/Error/401.page'
import Header from 'src/components/Layout/Header/Header'
import Pagination from 'src/components/Pagination/Pagination'
import NotFound from 'src/components/Error/NotFound'

const channelService = new ChannelService()

interface IProps extends RouteComponentProps {
  /** User from state.user */
  user: IUser
  /** User permissions checker */
  userCan: (permission: string) => boolean
}

const Channels: React.FC<IProps> = ({ user, userCan, history }) => {
  const _isMounted = React.useRef(false)

  // indicators
  const [isLoading, setIsLoading] = useState<boolean>(true)

  // All channels
  const [channels, setChannels] = useState<IChannel[]>()

  const [searchTerm, setSearchTerm] = useState<string>()
  const [page, setPage] = useState(1)

  useEffect(() => {
    _isMounted.current = true
    return () => {
      _isMounted.current = false
    }
  }, [])

  useEffect(() => {
    if (userCan('view_channels')) {
      channelService
        .fetch()
        .then((channels) => {
          if (_isMounted.current && channels.length) {
            setChannels(channels)
          }
        })
        .finally(() => _isMounted.current && setIsLoading(false))
    }
  }, [userCan, user.manufacturer_id])

  const filteredChannels = React.useMemo(() => {
    setPage(1)
    let filteredChannels = channels || []

    if (filteredChannels.length) {
      if (searchTerm) {
        filteredChannels = filteredChannels.filter((channel) => {
          return (
            basicSearch(channel.name, searchTerm) ||
            basicSearch(channel.status, searchTerm)
          )
        })
      }
    }

    return filteredChannels
  }, [channels, searchTerm])

  /**
   * Get the channels to be shown on the current page
   */
  const paginatedChannels = React.useMemo(() => {
    if (!filteredChannels) return []
    else return paginate(filteredChannels, page)
  }, [page, filteredChannels])

  if (!userCan('view_channels')) return <Error401 />

  return (
    <>
      <Header title="Channels" />
      <div className="page-container">
        <Container>
          <Card>
            <Card.Header>
              <div className="w-100 d-flex align-items-center justify-content-between">
                <div className="d-flex align-items-center">
                  <div>
                    {filteredChannels
                      ? `${filteredChannels.length} ${pluralize(
                          'channel',
                          filteredChannels.length
                        )}`
                      : '0 channels'}
                  </div>
                  <div className="ml-3">
                    <Form.Control
                      size="sm"
                      type="text"
                      name="searchTerm"
                      value={searchTerm || ''}
                      className="search"
                      style={{ minWidth: '250px' }}
                      placeholder="Search by name or status"
                      onChange={({ target }) => setSearchTerm(target.value)}
                      disabled={isLoading || !filteredChannels}
                    />
                  </div>
                </div>

                {userCan('create_channel') && (
                  <div>
                    <Link to={route('channel_create')}>
                      <Button size="sm">Create Channel</Button>
                    </Link>
                  </div>
                )}
              </div>
            </Card.Header>
            <Card.Body className="px-0 py-0">
              {!isLoading && paginatedChannels.length ? (
                <>
                  <div className="table-wrapper padded">
                    <Table hover responsive>
                      <thead>
                        <tr>
                          <th className="text-center">#</th>
                          <th className="sticky">Name</th>
                          <th>Status</th>
                          {/* <th>Products</th> */}
                          <th>Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {paginatedChannels.map((channel, index) => (
                          <tr
                            key={channel._id}
                            className="cursor-pointer"
                            onClick={() =>
                              history.push(
                                route('channel_update', {
                                  id: channel._id,
                                })
                              )
                            }
                          >
                            <td className="align-middle text-center">
                              {index + 1 + (page - 1) * 10}
                            </td>
                            <td className="align-middle">{channel.name}</td>
                            <td className="align-middle">
                              <Badge
                                className="text-capitalize"
                                variant={
                                  channel.status === 'active'
                                    ? 'success'
                                    : 'danger'
                                }
                              >
                                {channel.status}
                              </Badge>
                            </td>
                            {/* <td>
                            <Link
                              to={route('channel_products', {
                                id: channel._id,
                              })}
                            >
                              Products
                            </Link>
                          </td> */}
                            <td className="align-middle">
                              <Link
                                to={route('channel_update', {
                                  id: channel._id,
                                })}
                                className="action-link"
                              >
                                Edit
                              </Link>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>
                  <Pagination
                    className="my-4"
                    perPage={10}
                    totalItems={filteredChannels.length}
                    currentPage={page}
                    onChange={(page) => setPage(page)}
                  />
                </>
              ) : null}

              {!isLoading && !paginatedChannels.length ? (
                <div className="my-5">
                  <NotFound summary="No channels found" />
                </div>
              ) : null}

              {isLoading && (
                <div className="d-flex justify-content-center py-5">
                  <figure className="spinner primary" />
                </div>
              )}
            </Card.Body>
          </Card>
        </Container>
      </div>
    </>
  )
}

export default connect(
  (state: IState) => ({
    user: state.user,
  }),
  { userCan }
)(withRouter(Channels))
